/**
 * useConsensusOptions — v1.15.x P3.3
 *
 * Reads the user's `consensusBookPolicy` + `consensusExcludedBooks` preferences
 * out of AuthContext.userProfile.preferences (with safe defaults that match
 * `website/constants/defaultPreferences.js`) and returns a memoized object
 * shaped exactly the way `canonicalEv.js::resolveConsensusFair` expects:
 *
 *     { policy: 'default' | 'include_all' | 'custom', excluded: Set<string> }
 *
 * Screens/components that want to honour the user's override pass the result
 * straight into `getCanonicalSortEv(option, null, opts)` /
 * `cleanPerBookEv(raw, bd, isSGP, opts)` / `computeBookEvFromConsensus(raw, p, opts)`.
 *
 * When `policy === 'default'`, these functions fall back to the pre-P3 path
 * (backend-stamped `consensus_fair_*`) so guarding calls with this hook is
 * zero-regression for users who haven't changed the setting.
 */
import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSiteConfig } from '../context/SiteConfigContext';
import { defaultPreferences } from '../constants/defaultPreferences';

const VALID_POLICIES = new Set(['default', 'include_all', 'custom']);

export function useConsensusOptions() {
    const { userProfile } = useAuth();
    const { sgpMathV2Enabled } = useSiteConfig();
    const prefs = userProfile?.preferences;

    return useMemo(() => {
        const rawPolicy = prefs?.consensusBookPolicy ?? defaultPreferences.consensusBookPolicy;
        const policy = VALID_POLICIES.has(rawPolicy) ? rawPolicy : 'default';
        const rawExcluded = Array.isArray(prefs?.consensusExcludedBooks)
            ? prefs.consensusExcludedBooks
            : defaultPreferences.consensusExcludedBooks;
        const excluded = new Set(
            rawExcluded
                .filter((v) => typeof v === 'string' && v.trim().length > 0)
                .map((v) => v.toLowerCase())
        );
        return { policy, excluded, sgpMathV2: sgpMathV2Enabled !== false };
        // Shallow-compare the preference fields we actually use; ignore the rest
        // of userProfile so unrelated profile changes don't thrash consumers.
    }, [prefs?.consensusBookPolicy, prefs?.consensusExcludedBooks, sgpMathV2Enabled]);
}

/**
 * Non-hook variant for places outside React render context (e.g. a utility
 * pipeline that already has the preferences object). Keeps the shape in sync.
 */
export function resolveConsensusOptionsFromPrefs(prefs) {
    const rawPolicy = prefs?.consensusBookPolicy ?? defaultPreferences.consensusBookPolicy;
    const policy = VALID_POLICIES.has(rawPolicy) ? rawPolicy : 'default';
    const rawExcluded = Array.isArray(prefs?.consensusExcludedBooks)
        ? prefs.consensusExcludedBooks
        : defaultPreferences.consensusExcludedBooks;
    const excluded = new Set(
        rawExcluded
            .filter((v) => typeof v === 'string' && v.trim().length > 0)
            .map((v) => v.toLowerCase())
    );
    return { policy, excluded };
}
